/**
 * Documentos internos (admin only)
 *
 * Lista los documentos estratégicos del proyecto (recall, study SaaS, changelog, PRD)
 * y permite descargarlos como PDF (WeasyPrint backend) o verlos en HTML inline.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { FileText, Download, Loader2, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function DocsInternos() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerHtml, setViewerHtml] = useState("");
  const [viewerTitle, setViewerTitle] = useState("");

  useEffect(() => {
    api.get("/docs").then(({ data }) => {
      setDocs(data);
      setLoading(false);
    });
  }, []);

  const descargarPdf = async (slug, titulo) => {
    try {
      const { data } = await api.get(`/docs/${slug}/pdf`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Liberar memoria tras la descarga
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (e) {
      alert("No se pudo descargar el PDF: " + (e?.response?.data?.detail || e.message));
    }
  };

  const verHtml = async (slug, titulo) => {
    setViewerTitle(titulo);
    setViewerHtml("Cargando…");
    setViewerOpen(true);
    const { data } = await api.get(`/docs/${slug}`);
    setViewerHtml(data.html);
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <PageHeader
        icon={FileText}
        title="Documentos internos"
        description="Estudios, recalls y documentación estratégica del proyecto. Solo admin."
      />

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
        </div>
      ) : (
        <div className="space-y-3 mt-4" data-testid="docs-list">
          {docs.map((d) => (
            <div
              key={d.slug}
              className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-4 hover:border-amber-500/30 transition-colors"
              data-testid={`doc-${d.slug}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-amber-300 font-semibold">
                    <FileText className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{d.titulo}</span>
                  </div>
                  <p className="text-sm text-zinc-400 mt-1">{d.descripcion}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => verHtml(d.slug, d.titulo)}
                    data-testid={`doc-view-${d.slug}`}
                    className="border-zinc-700"
                  >
                    <Eye className="w-4 h-4 mr-1" /> Ver
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => descargarPdf(d.slug, d.titulo)}
                    data-testid={`doc-pdf-${d.slug}`}
                    className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold"
                  >
                    <Download className="w-4 h-4 mr-1" /> PDF
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white text-zinc-900">
          <DialogHeader>
            <DialogTitle>{viewerTitle}</DialogTitle>
          </DialogHeader>
          <style>{`
            .doc-html h1 { color: #b45309; border-bottom: 2px solid #f59e0b; padding-bottom: 0.5rem; font-size: 1.5rem; margin-bottom: 1rem; }
            .doc-html h2 { color: #92400e; font-size: 1.15rem; margin-top: 1.5rem; border-bottom: 1px solid #fde68a; padding-bottom: 0.25rem; }
            .doc-html h3 { color: #78350f; font-size: 1rem; margin-top: 1rem; }
            .doc-html p, .doc-html li { font-size: 0.9rem; color: #3f3f46; line-height: 1.55; }
            .doc-html ul, .doc-html ol { padding-left: 1.5rem; margin: 0.5rem 0; }
            .doc-html code { background: #f4f4f5; padding: 0.1rem 0.35rem; border-radius: 0.25rem; font-size: 0.8rem; }
            .doc-html pre { background: #18181b; color: #e4e4e7; padding: 0.75rem; border-radius: 0.4rem; overflow-x: auto; font-size: 0.78rem; }
            .doc-html blockquote { border-left: 3px solid #f59e0b; background: #fffbeb; padding: 0.5rem 0.75rem; margin: 0.5rem 0; color: #52525b; }
            .doc-html table { border-collapse: collapse; width: 100%; margin: 0.6rem 0; font-size: 0.8rem; }
            .doc-html th { background: #fef3c7; color: #78350f; padding: 0.35rem 0.5rem; border: 1px solid #fde68a; text-align: left; }
            .doc-html td { padding: 0.35rem 0.5rem; border: 1px solid #e4e4e7; }
            .doc-html hr { border: 0; border-top: 1px solid #e4e4e7; margin: 1rem 0; }
            .doc-html strong { color: #18181b; }
          `}</style>
          <div className="doc-html" dangerouslySetInnerHTML={{ __html: viewerHtml }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
